import { AuthContext } from 'context/AuthContext';
import ErrorBoundaryContext from 'context/ErrorBoundaryContext';
import { apiRequest } from 'helpers/api';
import { useContext, useEffect, useState } from 'react';
import type { OnboardingFlow } from 'types/restaurant';

export const STATUSES = {
  DISABLED: 'disabled',
  ERRORED: 'errored',
  UNAUTHORIZED: 'unauthorized',
  LOADED: 'loaded',
  LOADING: 'loading',
} as const;

export type UseLocationStatus = (typeof STATUSES)[keyof typeof STATUSES];

interface UseLocationOptions {
  enabled?: boolean;
}

export interface OrderingTheme {
  light?: { surface_color?: { value?: string; label?: string } };
}

export interface Location {
  id: string;
  name: string;
  obn_commission_percent: number | null;
  transaction_fee: number | null;
  transaction_percent: number | null;
  marketplace_diner_fee_percent: number | null;
  marketplace_restaurant_commission_percent: number | null;
  onboarding_flow?: OnboardingFlow;
  ordering_theme?: OrderingTheme;
  catering?: {
    settings: {
      commission_percent: number | null;
    };
  };
}

export interface AdminLocationResponse {
  data: {
    id: string;
    name: string;
    obn_commission_percent: number | null;
    transaction_fee: number | null;
    transaction_percent: number | null;
    marketplace_diner_fee_percent: number | null;
    marketplace_restaurant_commission_percent: number | null;
    onboarding_flow?: OnboardingFlow;
    ordering_theme?: OrderingTheme;
    catering?: {
      settings: {
        commission_percent: number | null;
      };
    };
  };
  metadata: {
    scopes: string[];
    obn_commission_percent_options: number[];
  };
}

export default function useLocation(
  locationId: string | undefined,
  { enabled = true }: UseLocationOptions = {},
) {
  const [status, setStatus] = useState<UseLocationStatus>(
    enabled ? STATUSES.LOADING : STATUSES.DISABLED,
  );
  const [location, setLocation] = useState<
    Location & { metadata?: AdminLocationResponse['metadata'] }
  >();

  const { user } = useContext(AuthContext);
  const { setError } = useContext(ErrorBoundaryContext);

  const refreshLocation = async (
    controller?: AbortController,
    { silent = false }: { silent?: boolean } = {},
  ) => {
    if (!user || !locationId || !enabled) {
      if (!silent) {
        setLocation(undefined);
        setStatus(enabled ? STATUSES.LOADED : STATUSES.DISABLED);
      }
      return;
    }

    if (!silent) setStatus(STATUSES.LOADING);

    const { data: locationData } = await apiRequest<Location>({
      controller,
      path: `restaurant/${locationId}`,
      onError: (response, err) => {
        if (!silent) setLocation(undefined);

        if (
          !silent &&
          controller?.signal.aborted &&
          err instanceof Error &&
          err?.name === 'AbortError'
        ) {
          setStatus(STATUSES.LOADING);
        } else if (!silent) {
          setStatus(response && response.status === 403 ? STATUSES.UNAUTHORIZED : STATUSES.ERRORED);
        }
      },
    });

    if (!locationData) return;

    const { data: adminData } = await apiRequest<AdminLocationResponse>({
      controller,
      path: `restaurant/${locationId}`,
      customHost: import.meta.env.VITE_ADMIN_API_HOST,
      setError,
    });

    setLocation({
      ...locationData,
      ...adminData?.data,
      metadata: adminData?.metadata,
    });
    if (!silent) setStatus(STATUSES.LOADED);
  };

  // Refreshes location data without triggering the LOADING status, so consumers
  // that gate on isLocationLoading (e.g. LocationDetailsLayout) don't unmount.
  const silentRefreshLocation = () => refreshLocation(undefined, { silent: true });

  useEffect(() => {
    const controller = new AbortController();

    void refreshLocation(controller);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, locationId, setError, user]);

  return { location, status, refreshLocation, silentRefreshLocation };
}
